/**
 * 기타비용 모달 (v1.5.0) — 영업건 직접경비 (숙박비·운송비 등) 자유입력.
 *
 * 진입: 영업 상세 / 판매보고서 모달의 [+ 기타비용] 버튼.
 * 목록 + 추가/수정/삭제. 금액은 VAT 별도 공급가 기준 (매입 금액과 동일 기준).
 * 금액 칸은 엑셀식 `=` 수식 지원 (=50000*2 등, evalAmount 공용 평가기).
 *   Enter 또는 다른 칸 클릭(blur) 시 계산값으로 확정된다 (commitAmount).
 * 저장 후 syncAllData 로 영업 상세·매출분석·대시보드 자동 갱신.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as salesApi from '../lib/sales-api';
import { syncAllData } from '../lib/invalidate';
import { normalizeAmountInput } from '../lib/evalAmount';
import { DateInput } from './DateInput';
import type { SalesMiscCost } from '../lib/types';
import { useDialogLayer } from '../lib/dialog-stack';

interface Props {
  salesId: string;
  miscCosts: SalesMiscCost[];
  onClose: () => void;
}

function fmtKrw(n: number): string {
  return Math.round(n).toLocaleString('ko-KR');
}

const EMPTY_FORM = { label: '', amount: '', costDate: '', note: '' };

export function MiscCostModal({ salesId, miscCosts, onClose }: Props) {
  // 입력 창 — ESC 로 안 닫힌다 · 아래 창으로도 새지 않는다 (v2.231.0 · H-4 · lib/dialog-stack.ts)
  useDialogLayer(true, null);
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const total = miscCosts.reduce((a, m) => a + m.amount, 0);

  function resetForm() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setError(null);
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      // 금액 = 엑셀식 수식 지원 (=50000*2, =(3+2)*10000 등). 실패 시 null.
      const norm = normalizeAmountInput(form.amount);
      const amount = norm === null ? NaN : Number(norm);
      if (!form.label.trim()) throw new Error('항목명을 입력하세요 (예: 숙박비, 운송비)');
      if (norm === null) throw new Error('금액 형식이 올바르지 않습니다 (수식은 =50000*2 처럼 = 로 시작)');
      if (!Number.isFinite(amount) || amount === 0) throw new Error('금액을 입력하세요 (0 제외)');
      const input = {
        label: form.label.trim(),
        amount,
        costDate: form.costDate || null,
        note: form.note.trim() || null,
      };
      if (editingId !== null) {
        await salesApi.updateMiscCost(salesId, editingId, input);
      } else {
        await salesApi.addMiscCost(salesId, input);
      }
    },
    onSuccess: () => {
      resetForm();
      syncAllData(qc); // 영업 상세(detail)·매출분석·대시보드 갱신
    },
    onError: (e: Error) => setError(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => salesApi.deleteMiscCost(salesId, id),
    onSuccess: () => {
      resetForm();
      syncAllData(qc);
    },
    onError: (e: Error) => setError(e.message),
  });

  /**
   * 금액 칸의 수식/콤마 입력을 계산된 숫자로 확정 (=50000*2 → 100,000).
   * Enter·다른 칸 클릭(blur) 두 곳에서 같이 쓴다.
   * (2026-08-20 v2.77.7 — Enter 처리가 빠져 있어 계산값이 아래 안내에만 보이고
   *  입력칸에는 반영되지 않던 것을 고침. 다른 금액 칸들과 동작을 맞춤.)
   * 식 오류·빈칸이면 그대로 둔다 (아래 안내가 「식 오류」를 이미 보여준다).
   */
  function commitAmount() {
    setForm((f) => {
      const norm = normalizeAmountInput(f.amount);
      if (norm === null || f.amount.trim() === '') return f;
      return { ...f, amount: fmtKrw(Number(norm)) };
    });
  }

  function startEdit(m: SalesMiscCost) {
    setEditingId(m.id);
    setForm({
      label: m.label,
      amount: fmtKrw(m.amount),
      costDate: m.costDate ?? '',
      note: m.note ?? '',
    });
    setError(null);
  }

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
    >
      <div
        className="bg-white rounded shadow-xl w-[620px] max-w-[95vw] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t">
          <div>
            <h3 className="ui-subsection-title">기타비용 — {salesId}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              숙박비·운송비 등 매입 외 직접경비. 이 영업건의 원가에 합산됩니다 (VAT 별도 금액으로 입력).
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="ui-close"
          >
            ×
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-4">
          {/* 목록 */}
          <table className="data-table w-full text-[13px]">
            <thead>
              <tr>
                <th className="text-left">지출일</th>
                <th className="text-left">항목</th>
                <th className="text-left">비고</th>
                <th className="text-right">금액</th>
                <th className="text-center w-24">작업</th>
              </tr>
            </thead>
            <tbody>
              {miscCosts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-slate-500 py-5 text-xs">
                    등록된 기타비용이 없습니다. 아래에서 추가하세요.
                  </td>
                </tr>
              ) : (
                miscCosts.map((m) => (
                  <tr key={m.id} className={editingId === m.id ? 'bg-amber-50' : ''}>
                    <td className="text-xs">{m.costDate ?? '-'}</td>
                    <td className="font-medium">{m.label}</td>
                    <td className="text-xs text-slate-500">{m.note ?? ''}</td>
                    <td className="text-right num font-semibold">{fmtKrw(m.amount)}</td>
                    <td className="text-center">
                      <button
                        type="button"
                        onClick={() => startEdit(m)}
                        className="btn btn-secondary btn-sm mr-1"
                      >
                        수정
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!window.confirm(`[${m.label} ${fmtKrw(m.amount)}원] 을 삭제할까요?`)) return;
                          deleteMut.mutate(m.id);
                        }}
                        disabled={deleteMut.isPending}
                        className="btn btn-danger btn-sm"
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))
              )}
              {miscCosts.length > 0 && (
                <tr className="bg-slate-50 font-bold">
                  <td colSpan={3} className="text-right">기타비용 합계</td>
                  <td className="text-right num">{fmtKrw(total)}</td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>

          {/* 입력 폼 */}
          <div className="border border-slate-200 rounded p-4 bg-slate-50/60">
            <div className="text-sm font-semibold text-slate-700 mb-2">
              {editingId !== null ? `항목 수정 (#${editingId})` : '새 항목 추가'}
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <label className="ui-label">항목명 *</label>
                <input
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                  placeholder="예: 숙박비 / 운송비 / 출장경비"
                  className="ui-input w-full mt-1"
                />
              </div>
              <div>
                <label className="ui-label">금액 (원, VAT 별도) *</label>
                <input
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  onBlur={commitAmount}
                  onKeyDown={(e) => {
                    // Enter 로도 계산 확정 (한글 조합 중 Enter 는 제외)
                    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      commitAmount();
                    }
                  }}
                  placeholder="예: 150000 또는 =50000*2"
                  title="=50000*2, =(3+2)*10000 같은 수식 입력 가능 (Enter·다른 칸 클릭 시 계산)"
                  className="ui-input w-full mt-1 text-right"
                />
                {form.amount.trim().startsWith('=') && (() => {
                  const norm = normalizeAmountInput(form.amount);
                  return (
                    <div
                      className={`text-xs mt-0.5 text-right ${
                        norm === null ? 'text-rose-500' : 'text-emerald-600'
                      }`}
                    >
                      {norm === null ? '식 오류' : `= ${fmtKrw(Number(norm))} 원`}
                    </div>
                  );
                })()}
              </div>
              <div>
                <label className="ui-label">지출일 (선택)</label>
                <DateInput
                  value={form.costDate}
                  onChange={(v) => setForm((f) => ({ ...f, costDate: v }))}
                  className="mt-1 w-full"
                  inputClassName="border border-slate-300 rounded px-2 py-1.5 text-sm outline-none focus:border-blue-500"
                  placeholder="YYYY-MM-DD (예: 20260512)"
                />
              </div>
              <div>
                <label className="ui-label">비고 (선택)</label>
                <input
                  value={form.note}
                  onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="예: ○○호텔 2박, 화물 운송"
                  className="ui-input w-full mt-1"
                />
              </div>
            </div>
            {error && <div className="text-xs text-red-600 mt-2">{error}</div>}
            <div className="flex items-center justify-end gap-2 mt-3">
              {editingId !== null && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn btn-secondary"
                >
                  수정 취소
                </button>
              )}
              <button
                type="button"
                onClick={() => saveMut.mutate()}
                disabled={saveMut.isPending}
                className="btn btn-primary"
              >
                {saveMut.isPending ? '저장 중…' : editingId !== null ? '수정 저장' : '+ 추가'}
              </button>
            </div>
          </div>

          <p className="text-xs text-slate-500 leading-snug">
            ※ 기타비용은 매입관리 목록에는 나타나지 않고, 이 영업건의 원가(이익 계산)와
            매출분석·월간보고서 손익에만 합산됩니다. 세금계산서가 있는 지출은 매입관리에 등록하세요.
          </p>
        </div>
      </div>
    </div>
  );
}
